/**
 * Turning table state into table noises.
 *
 * The rule this hook exists to enforce: sound follows the authoritative state,
 * it never follows a click. If a cue were fired from the button that submits a
 * discard, then in Online a rejected action would still make a noise, and a
 * discard arriving from another player would make none. Watching the state
 * instead means every seat sounds the same, the local player included, and a
 * move that never happened is never heard.
 *
 * Each cue is tied to a value that only changes when the thing it describes
 * actually happened, so React re-rendering for any other reason - a bot
 * thinking, the language changing, a panel opening - is silent.
 */

import { useEffect, useRef } from "react";
import type { TableModel } from "./tableModel";
import { playCue } from "./tableAudio";

export function useTableSound(model: TableModel): void {
  const lastDiscardId = useRef<number | null>(null);
  const drawnId = useRef<number | null>(null);
  const claiming = useRef(false);
  const ended = useRef(false);

  // A tile landing on the felt - anyone's. The id changes exactly once per
  // discard, so a re-render during the claim window that follows is silent.
  const discardId = model.lastDiscard?.tile.id ?? null;
  useEffect(() => {
    if (discardId !== null && discardId !== lastDiscardId.current) playCue("discard");
    lastDiscardId.current = discardId;
  }, [discardId]);

  // A tile arriving in your own hand from the wall.
  const drawn = model.drawnTile?.id ?? null;
  useEffect(() => {
    if (drawn !== null && drawn !== drawnId.current) playCue("draw");
    drawnId.current = drawn;
  }, [drawn]);

  // Somebody may claim: the table has stopped and is waiting on a person.
  const isClaiming = model.phase === "awaiting-claims";
  useEffect(() => {
    if (isClaiming && !claiming.current) playCue("claim");
    claiming.current = isClaiming;
  }, [isClaiming]);

  // The hand is over. A draw ends as quietly as it deserves to.
  const isWin = model.result?.type === "win";
  const isOver = model.result !== null;
  useEffect(() => {
    if (isOver && !ended.current && isWin) playCue("win");
    ended.current = isOver;
  }, [isOver, isWin]);
}
